import React, { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { window } from "mixpanel-browser/src/window";
import { Form, Input, Select, DatePicker, message, Button } from "antd";
import dayjs from "dayjs";

import { useJobCreation } from "./JobCreationContext";
import JobButtons from "./Job-Buttons";
import "./Styles/CreateJob-Basics.css";

const { Option } = Select;

export default function CreateJobBasics() {
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state || {};
  const { jobData, updateJobData } = useJobCreation();
  const jobEdit = state.jobEdit || false;
  const passedJobData = state.jobData || {};

  useEffect(() => {
    let basics = {};

    if (jobEdit) {
      basics = { ...(passedJobData.basics || {}) };

      if (basics.category !== undefined) {
        basics.category = String(basics.category);
      }

      if (passedJobData?.id) {
        updateJobData("id", passedJobData.id);
        updateJobData("compensation", passedJobData.compensation || {});
        updateJobData("description", passedJobData.description || "");
        updateJobData("media", passedJobData.media || {});
      }
    } else {
      basics = { ...(jobData.basics || {}) };

      if (basics.category !== undefined) {
        basics.category = String(basics.category);
      }
    }

    // Handle expiration date
    let expirationValue: any = null;
    let useNAExpiration = basics.useNAExpiration || false;

    if (basics.expirationDate) {
      if (basics.expirationDate === "N/A") {
        expirationValue = null;
        useNAExpiration = true;
      } else if (dayjs(basics.expirationDate, "YYYY-MM-DD", true).isValid()) {
        expirationValue = dayjs(basics.expirationDate, "YYYY-MM-DD");
      }
    }

    // update form
    form.setFieldsValue({
      ...basics,
      expirationDate: expirationValue,
      useNAExpiration,
    });

    //eslint-disable-next-line  react-hooks/exhaustive-deps
  }, [jobEdit, passedJobData, jobData.basics, form]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleCategoryChange = (value) => {
    form.setFieldValue("category", value);
    updateJobData("basics", { ...form.getFieldsValue(), category: value });
  };

  const handleNext = async () => {
    try {
      const values = await form.validateFields();

      let payload = { ...values };

      if (values.useNAExpiration) {
        payload.expirationDate = "N/A";
        payload.useNAExpiration = true;
      } else if (values.expirationDate) {
        payload.expirationDate = dayjs(values.expirationDate).format("YYYY-MM-DD");
        payload.useNAExpiration = false;
      } else {
        delete payload.expirationDate;
        payload.useNAExpiration = false;
      }

      updateJobData("basics", payload);

      navigate("compensation", {
        state: { jobEdit },
      });
    } catch (err) {
      message.error("Please fix the errors in the form.");
    }
  };

  return (
    <>
      <div className="job-create-head">
        <h2 className="job-create-title">Job Basic</h2>
      </div>
      <div className="job-create-container">
        <Form
          form={form}
          layout="vertical"
          onFinish={handleNext}
          className="job-create-form"
        >
          <Form.Item
            label="Job Title"
            name="title"
            rules={[
              { required: true, message: "Job Title is required" },
              { max: 50, message: "Job Title must be 50 characters or less" },
            ]}
          >
            <Input placeholder="e.g. Chief Marketing Executive" />
          </Form.Item>

          <Form.Item
            label="Location"
            name="location"
            rules={[
              { required: true, message: "Location is required" },
              { max: 50, message: "Location must be 50 characters or less" },
            ]}
          >
            <Input placeholder="e.g. Ikeja, Lagos" />
          </Form.Item>

          <Form.Item
            label="Job Type"
            name="job_type"
            rules={[{ required: true, message: "Please select a job type" }]}
          >
            <Select placeholder="Select Job Type">
              <Option value="full_time">Full Time</Option>
              <Option value="part_time">Part Time</Option>
              <Option value="contract">Contract</Option>
              <Option value="internship">Internship</Option>
              <Option value="remote">Remote</Option>
              <Option value="hybrid">Hybrid</Option>
            </Select>
          </Form.Item>

          <Form.Item
            label="Category"
            name="category"
            rules={[{ required: true, message: "Please select a category" }]}
          >
            <Select
              placeholder="Select Category"
              onChange={handleCategoryChange}
            >
              <Option value="2">Admin & Office</Option>
              <Option value="3">Art & Design</Option>
              <Option value="4">Business Operations</Option>
              <Option value="5">Cleaning & Facilities</Option>
              <Option value="6">Community & Social Services</Option>
              <Option value="7">Computer & Data</Option>
              <Option value="8">Construction & Mining</Option>
              <Option value="9">Education</Option>
              <Option value="10">Farming & Forestry</Option>
              <Option value="11">Healthcare</Option>
              <Option value="12">Installation, Maintenance & Repair</Option>
              <Option value="13">Legal</Option>
              <Option value="14">Management</Option>
              <Option value="15">Manufacturing</Option>
              <Option value="16">Media & Communication</Option>
              <Option value="17">Personal Care</Option>
              <Option value="18">Protective Services</Option>
              <Option value="19">Restaurant & Hospitality</Option>
              <Option value="20">Retail & Sales</Option>
              <Option value="21">Science & Engineering</Option>
              <Option value="22">Sports & Entertainment</Option>
              <Option value="23">Transportation</Option>
              <Option value="1">Others</Option>
            </Select>
          </Form.Item>

          <Form.Item
            label={
              <>
                Expiration Date{" "}
                <span
                  style={{
                    color: "#999",
                    fontSize: "0.85em",
                    marginLeft: "5px",
                  }}
                >
                  (Optional)
                </span>
              </>
            }
            name="expirationDate"
          >
            {/* Listen to expirationDate + useNAExpiration */}
            <Form.Item
              noStyle
              shouldUpdate={(prev, cur) =>
                prev.expirationDate !== cur.expirationDate ||
                prev.useNAExpiration !== cur.useNAExpiration
              }
            >
              {({ getFieldValue }) => {
                const pickedDate = getFieldValue("expirationDate");
                const isNA = getFieldValue("useNAExpiration");

                if (isNA) {
                  return (
                    <div
                      style={{
                        background: "#f5f5f5",
                        padding: "4px 8px",
                        borderRadius: "6px",
                        fontSize: "0.85em",
                        color: "#333",
                        width: "fit-content",
                        marginBottom: "8px",
                      }}
                    >
                      Picked Date: N/A
                    </div>
                  );
                }

                if (pickedDate) {
                  return (
                    <div
                      style={{
                        background: "#f5f5f5",
                        padding: "4px 8px",
                        borderRadius: "6px",
                        fontSize: "0.85em",
                        color: "#333",
                        width: "fit-content",
                        marginBottom: "8px",
                      }}
                    >
                      Picked Date: {dayjs(pickedDate).format("YYYY-MM-DD")}
                    </div>
                  );
                }

                return null;
              }}
            </Form.Item>

            <div
              style={{
                display: "flex",
                gap: "10px",
                alignItems: "center",
                marginTop: "10px",
              }}
            >
              <DatePicker
                style={{ width: "100%" }}
                format="YYYY-MM-DD"
                allowClear
                placeholder="Select expiration date (optional)"
                disabledDate={(current) => current && current <= dayjs().endOf("day")}
                disabled={form.getFieldValue("useNAExpiration")}
                onChange={(date) => {
                  form.setFieldsValue({ expirationDate: date });
                }}
              />

              {jobEdit && (
                <Button
                  type="link"
                  size="small"
                  onClick={() => {
                    const isNA = form.getFieldValue("useNAExpiration");
                    form.setFieldsValue({
                      expirationDate: null,
                      useNAExpiration: !isNA,
                    });
                  }}
                >
                  {form.getFieldValue("useNAExpiration")
                    ? "Use Date"
                    : "Clear (N/A)"}
                </Button>
              )}
            </div>
          </Form.Item>

          {/* hidden flag for N/A */}
          <Form.Item name="useNAExpiration" initialValue={false} hidden>
            <Input type="hidden" />
          </Form.Item>

          <JobButtons
            showPrevious={false}
            continueText={"Next"}
            onContinue={handleNext}
            disabled={false}
          />
        </Form>
      </div>
    </>
  );
}
